import { RevenueOrdersTable } from './revenue-orders-table';
import './revenue.scss';
import { BarChart } from '../../../components/charts/bar-chart';
import { PeriodicRevenue, ReportingPeriod } from '../../../clients/quipli-service/stats/stats';
import { DetailedHTMLProps, HTMLAttributes, ReactNode, useEffect, useState } from 'react';
import { useStatsApiV1 } from '../../../clients/quipli-service/stats/stats-api-v1';
import { toast } from '../../../util/toasts';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { displayMoneyFormat, safeRound } from '../../../util/util';
import { Spinner } from '../../../components/spinner/spinner';
import { Card } from '../../../components/card/card';
import _ from 'lodash';
import { DateTime, Interval } from 'luxon';
import classNames from 'classnames';
import { useAdmin } from '../../../hooks/use-admin-context';
import { Typography } from '../../../components/typography/typography';
import useLocalStorage from 'use-local-storage';
import { ButtonGroup } from '../../../components/button-group/button-group';
import { LocationSelect } from '../../location/select/location-select';
import { Location } from '../../../clients/quipli-service/location/location';
import { DatePicker } from '../../../components/date-picker/date-picker';
import { useReportApiV2 } from '../../../clients/quipli-service/report/report-api-v2';
import { DateTimeRange } from '../../../clients/quipli-service/report/report';
import { QuipliLink } from '../../../components/link/quipli-link';
import { Button } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import { FilterField } from '../../../components/field/filter-field';
import { RevenueExportModal } from './revenue-export-modal';

export const Revenue = ({ showTable = true }) => {
  const admin = useAdmin();
  const hasStatsReporting = admin.hasPermissions('Sales Stats & Revenue Reporting - Dashboard');
  const { getAvailableTaxReportRange } = useReportApiV2();
  const { getPeriodicRevenue } = useStatsApiV1();
  const { getMultipleConfigurations } = useAPI();
  const [periodicRevenue, setPeriodicRevenue] = useState<PeriodicRevenue[]>();
  const [monthlyRevenue, setMonthlyRevenue] = useState<PeriodicRevenue[]>();
  const [currency, setCurrency] = useState<string>();
  const [timezone, setTimezone] = useState<string>();
  const [period, setPeriod] = useLocalStorage<ReportingPeriod>('period', 'month');
  const [basis, setBasis] = useLocalStorage<'accrual' | 'cash'>('basis', 'accrual');
  const [location, setLocation] = useState<Location | null>();
  const [from, setFrom] = useState<DateTime>(DateTime.now().startOf('year'));
  const [to, setTo] = useState<DateTime | undefined>(DateTime.now().endOf('day'));
  const [reportRange, setReportRange] = useState<{ from: DateTime; to: DateTime }>();
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);

  useEffect(() => {
    // @ts-expect-error update cache
    if (basis === 'invoice') {
      setBasis('accrual');
    }
    // @ts-expect-error update cache
    else if (basis === 'payment') {
      setBasis('cash');
    }
    getMultipleConfigurations(['CONF_CURRENCY', 'CONF_TIMEZONE']).then((confs) => {
      setCurrency(confs.find((c) => c.variable === 'CONF_CURRENCY')?.value);
      setTimezone(confs.find((c) => c.variable === 'CONF_TIMEZONE')?.value);
    });
    getAvailableTaxReportRange(basis).then((range) => {
      setReportRange(range);
    });
  }, []);

  useEffect(() => {
    if (hasStatsReporting) {
      getPeriodicRevenue({
        start: from,
        end: to ?? undefined,
        period,
        basis,
        locationId: location?.id ?? undefined,
      })
        .then(setPeriodicRevenue)
        .catch((ex) => toast(ex.message, 'error'));
      getPeriodicRevenue({
        period: 'month',
        basis,
        locationId: location?.id ?? undefined,
        start: DateTime.now().startOf('year'),
      })
        .then(setMonthlyRevenue)
        .catch((ex) => toast(ex.message, 'error'));
    }
  }, [timezone, hasStatsReporting, period, basis, location, from, to, reportRange]);

  const reportingMonths: DateTimeRange[] = [];
  if (reportRange) {
    let currentStart = reportRange.from.startOf('month').startOf('day');
    while (currentStart.diffNow().milliseconds < 0) {
      reportingMonths.push({ from: currentStart, to: currentStart.endOf('month').endOf('day') });
      currentStart = currentStart.plus({ months: 1 }).setZone(timezone, { keepLocalTime: true });
    }
  }
  reportingMonths.reverse();
  let selectedMonth = -1;
  if (from && to) {
    selectedMonth = reportingMonths.findIndex(
      (m) => m.from.diff(from!).milliseconds === 0 && m.to.diff(to!).milliseconds === 0,
    );
  }

  const selectedRangeDays = from && to ? to.diff(from).as('days') : 30;
  const buttonItems: { label: string; value: ReportingPeriod; hover?: string }[] = [
    ...(selectedRangeDays <= 60
      ? [{ label: 'Daily', value: 'day' as ReportingPeriod, hover: 'Granularity is limited to 31 days' }]
      : []),
    ...(selectedRangeDays <= 365
      ? [{ label: 'Weekly', value: 'week' as ReportingPeriod, hover: 'Granularity is limited to 52 weeks' }]
      : []),
    ...(selectedRangeDays >= 28
      ? [{ label: 'Monthly', value: 'month' as ReportingPeriod, hover: 'Granularity is limited to 12 months' }]
      : []),
    ...(selectedRangeDays >= 365 ? [{ label: 'Yearly', value: 'year' as ReportingPeriod }] : []),
  ];
  const basisItems: { label: string; value: 'accrual' | 'cash' }[] = [
    { label: 'Accrual', value: 'accrual' },
    { label: 'Cash', value: 'cash' },
  ];

  return (
    <div className="dashboard flex flex-column gap-4">
      <div className="flex flex-row justify-between">
        <div className="max-w-screen-xl">
          <h1 className="font-semibold text-[20px] leading-[30px] md:text-[24px] md:leading-[32px] font-plex mt-0 pt-2 mb-4">
            Revenue Report
          </h1>
        </div>
        <div className="flex flex-row gap-2 justify-start">
          <Button
            className="button button-secondary relative h-[46px]"
            onClick={() => setIsExportModalOpen(true)}
            disabled={isExportModalOpen}
          >
            {isExportModalOpen ? <Spinner /> : <Icon icon={'Export'} />}
            Export
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-3 items-start">
        <FilterField title="Month">
          <select
            className="h-fit text-[14px] w-full"
            value={selectedMonth}
            required={false}
            onChange={(e) => {
              const range = reportingMonths[parseInt(e.target.value)];
              setFrom(range.from);
              setTo(range.to.endOf('day'));
            }}
          >
            <option value={-1} disabled>
              Select a Month
            </option>
            {reportingMonths.map((m, i) => (
              <option key={i} value={i}>
                {m.from.toFormat('LLLL yyyy')}
              </option>
            ))}
          </select>
        </FilterField>

        <FilterField title="Start Date">
          <DatePicker
            value={from}
            onChange={(e) => {
              if (e) {
                setFrom(e);
              }
            }}
            clearable={false}
            datePickerWrapperClassName="!w-full"
            inputWrapperClassName="!w-full"
            inputClassName="!w-full"
          />
        </FilterField>

        <FilterField title="End Date">
          <DatePicker
            value={to}
            onChange={(e) => {
              if (e) {
                setTo(e.endOf('day'));
              } else {
                setTo(e);
              }
            }}
            disabledIntervals={from && [Interval.fromDateTimes(DateTime.fromMillis(0), from)]}
            clearable={true}
            datePickerWrapperClassName="!w-full"
            inputWrapperClassName="!w-full"
            inputClassName="!w-full"
          />
        </FilterField>

        <FilterField title="Location" titleWeight="semi-bold">
          <LocationSelect
            orderLocation={location}
            setLocation={setLocation}
            dataCy="location-select"
            dropdownProps={{ menuPortalTarget: document.body }}
            clearable={true}
            restoreLast={false}
          />
        </FilterField>

        <FilterField title="Granularity" titleWeight="semi-bold">
          <ButtonGroup<ReportingPeriod>
            items={buttonItems}
            onClick={(value) => setPeriod(value)}
            selected={period}
            className="h-[46px]"
          />
        </FilterField>

        <FilterField title="Basis" titleWeight="semi-bold">
          <ButtonGroup<'accrual' | 'cash'>
            items={basisItems}
            onClick={(value) => setBasis(value)}
            selected={basis}
            className="h-[46px]"
          />
        </FilterField>
      </div>

      {hasStatsReporting && (
        <div className="grow flex gap-4 overflow-x-auto min-h-fit">
          <StatCard
            className="w-1/4 min-w-fit"
            label="Year to Date Revenue"
            value={_.sum(
              monthlyRevenue
                ?.filter(
                  (m) =>
                    m.periodStart.diff(DateTime.local({ zone: m.periodStart.zone }).startOf('year')).milliseconds >= 0,
                )
                .map((m) => m.revenue),
            )}
            valueFormatter={(v) => CurrencyStatCardValueFormatter(v, currency)}
            statDetail={
              <ComparisonStatDetail
                currentValue={_.sum(
                  periodicRevenue
                    ?.filter(
                      (m) =>
                        m.periodStart.diff(DateTime.local({ zone: m.periodStart.zone }).startOf('year')).milliseconds >=
                        0,
                    )
                    .map((m) => m.revenue),
                )}
                previousValue={_.sum(
                  monthlyRevenue
                    ?.filter(
                      (m) =>
                        m.periodStart.diff(DateTime.local({ zone: m.periodStart.zone }).startOf('year')).milliseconds <
                          0 &&
                        m.periodStart.diff(
                          DateTime.local({ zone: m.periodStart.zone }).startOf('year').minus({ years: 1 }),
                        ).milliseconds >= 0,
                    )
                    .map((m) => m.revenue),
                )}
                label="vs last year"
              />
            }
          />
          <StatCard
            className="w-1/4 min-w-fit"
            label="Revenue"
            value={_.sum(periodicRevenue?.map((m) => m.revenue))}
            valueFormatter={(v) => CurrencyStatCardValueFormatter(v, currency)}
            statDetail={
              <div className="flex flex-col">
                <Typography type={'text-sm'}>Revenue does not include taxes</Typography>
              </div>
            }
          />
          <StatCard
            className="w-1/4 min-w-fit"
            label="Tax"
            value={_.sum(periodicRevenue?.map((m) => m.tax))}
            valueFormatter={(v) => CurrencyStatCardValueFormatter(v, currency)}
            statDetail={
              <Typography type="text-sm">
                <QuipliLink to={`accounting/accounts?from=${from}&to=${to}&basis=${basis}&accountType=Tax`}>
                  Tax Report
                </QuipliLink>
              </Typography>
            }
          />
          <StatCard
            className="w-1/4 min-w-fit"
            label="Non-taxable Revenue"
            value={_.sum(periodicRevenue?.map((m) => m.nonTaxableTotal))}
            valueFormatter={(v) => CurrencyStatCardValueFormatter(v, currency)}
          />
        </div>
      )}
      {hasStatsReporting && periodicRevenue && (
        <div className="grow flex gap-4 overflow-x-auto flex-wrap">
          <RevenueGraph
            periodicRevenue={periodicRevenue}
            currency={currency}
            setPeriod={setPeriod}
            period={period}
            basis={basis}
            setBasis={setBasis}
            setLocation={setLocation}
          />
          <RevenueTable revenue={periodicRevenue} currency={currency} period={period} />
        </div>
      )}
      <div>{showTable && <RevenueOrdersTable />}</div>

      <RevenueExportModal
        isOpen={isExportModalOpen}
        setIsOpen={() => setIsExportModalOpen(false)}
        from={from}
        to={to}
        period={period}
        basis={basis}
        location={location}
      />
    </div>
  );
};

const ChartContainer = ({ barChart }: { barChart: ReactNode }) => {
  return <div className="h-[15rem] w-full">{barChart ? barChart : <Spinner />}</div>;
};

const RevenueTable = ({
  revenue,
  currency,
  period,
}: {
  revenue: PeriodicRevenue[];
  currency?: string;
  period: ReportingPeriod;
}) => {
  return (
    <Card className="revenue-table grow">
      {revenue ? (
        <table className="w-full h-full text-[0.8rem]">
          <thead>
            <tr>
              <th className="h-12"></th>
              <th className="text-right">Revenue</th>
              <th className="text-right">Orders</th>
            </tr>
          </thead>
          <tbody>
            {revenue.slice(periodRules(period).slice).map((m) => {
              return (
                <tr>
                  <td>{m.periodStart.toFormat(periodRules(period).fmt)}</td>
                  <td className="text-right">{displayMoneyFormat(m.revenue, currency)}</td>
                  <td className="text-right">{m.count || 0}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <Spinner />
      )}
    </Card>
  );
};

const RevenueGraph = ({
  periodicRevenue,
  currency,
  period,
}: {
  periodicRevenue?: PeriodicRevenue[];
  currency?: string;
  setPeriod: (period: ReportingPeriod) => void;
  period: ReportingPeriod;
  basis: 'accrual' | 'cash';
  setBasis: (basis: 'accrual' | 'cash') => void;
  location?: Location;
  setLocation?: (l: Location | null) => void;
}) => {
  return (
    <>
      <Card className="flex flex-column gap-2 grow h-full overflow-x-scroll">
        <div className="min-w-[40rem]">
          <Typography type={'display-sm'}>Revenue</Typography>
          <ChartContainer
            barChart={
              periodicRevenue && (
                <BarChart
                  className="h-[15rem] mt-8"
                  data={periodicRevenue.slice(periodRules(period).slice).map((m) => {
                    return { label: m.periodStart.toFormat(periodRules(period).fmt), value: m.revenue };
                  })}
                  valueFormatter={(v) => displayMoneyFormat(v, currency)}
                />
              )
            }
          />
        </div>
      </Card>
      <Card className="flex flex-column gap-2 grow h-full overflow-x-scroll">
        <div className="min-w-[40rem]">
          <Typography type={'display-sm'}>Tax</Typography>
          <ChartContainer
            barChart={
              periodicRevenue && (
                <BarChart
                  className="h-[15rem] mt-8"
                  data={periodicRevenue.slice(periodRules(period).slice).map((m) => {
                    return { label: m.periodStart.toFormat(periodRules(period).fmt), value: m.tax };
                  })}
                  valueFormatter={(v) => displayMoneyFormat(v, currency)}
                />
              )
            }
          />
        </div>
      </Card>
    </>
  );
};

const DefaultStatCardValueFormatter = (value: number) => {
  return <div className="stat-card-value">{value}</div>;
};

const CurrencyStatCardValueFormatter = (value: number, currency?: string) => {
  const strValue = displayMoneyFormat(value, currency);
  return (
    <div className="stat-card-value">
      <span className="text-[1.1rem] pr-1">{strValue.substring(0, strValue.search(/\d/))}</span>
      <span>{strValue.substring(strValue.search(/\d/))}</span>
    </div>
  );
};

const ComparisonStatDetail = ({
  currentValue,
  previousValue,
  label,
}: {
  currentValue?: number;
  previousValue?: number;
  label: string;
}) => {
  if (currentValue && previousValue) {
    const diff = safeRound(((currentValue - previousValue) * 100) / previousValue);
    return (
      <div className="flex gap-1 items-center text-[var(--grey-500)]">
        <i
          className={classNames('fa text-[.8rem]', {
            'fa-arrow-up': diff >= 0,
            'fa-arrow-down': diff < 0,
            'text-[var(--success-500)]': diff >= 0,
            'text-[var(--error-500)]': diff < 0,
          })}
        />
        <span
          className={classNames({
            'text-[var(--success-500)]': diff >= 0,
            'text-[var(--error-500)]': diff < 0,
          })}
        >
          {diff}%
        </span>
        <span>{label}</span>
      </div>
    );
  } else {
    return <></>;
  }
};

export const StatCard = ({
  label,
  value,
  valueFormatter = DefaultStatCardValueFormatter,
  statDetail,
  className,
  ...props
}: {
  label: string;
  value: number;
  valueFormatter?: (v: number) => ReactNode;
  statDetail?: ReactNode;
} & DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>) => {
  return (
    <Card className={classNames('stat-card flex flex-column gap-2', className)} {...props}>
      <h6>{label}</h6>
      <div>{valueFormatter(value)}</div>
      {statDetail}
    </Card>
  );
};

const periodRules = (period: ReportingPeriod) => {
  switch (period) {
    case 'day':
      return { fmt: 'MMMM dd', slice: -31 };
    case 'week':
      return { fmt: 'MMMM dd ', slice: -52 };
    case 'month':
      return { fmt: 'MMMM yyyy', slice: -12 };
    case 'year':
      return { fmt: 'yyyy', slice: -10 };
  }
};
